import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useJob } from '@/hooks/useJobs';
import { useJobCompletion } from '@/hooks/useJobCompletion';
import { useDocuments } from '@/hooks/useDocuments';
import { DocumentViewer } from '@/components/files/DocumentViewer';
import { formatCurrency } from '@/lib/types/job';
import { REVIEW_REASON_CODES, ReviewReasonCode } from '@/lib/types/completion';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export default function JobReviewPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { job, loading: jobLoading } = useJob(jobId!);
  const { completion, reviewCompletion } = useJobCompletion(job?.id || '');
  const { documents } = useDocuments({
    entityType: 'completion',
    entityId: completion?.id || ''
  });
  const { toast } = useToast();

  const [reviewResult, setReviewResult] = useState<'accepted' | 'rejected' | ''>('');
  const [reasonCode, setReasonCode] = useState<ReviewReasonCode | ''>('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (jobLoading) {
    return <div className="p-6">Loading job details...</div>;
  }

  if (!job) {
    return <div className="p-6">Job not found</div>;
  }

  if (!completion) {
    return (
      <div className="p-6">
        <div className="text-center">
          <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Completion Submitted</h2>
          <p className="text-muted-foreground">
            The vendor hasn't submitted their work completion yet.
          </p>
        </div>
      </div>
    );
  }

  const getAutoAcceptCountdown = () => {
    const autoAcceptDate = new Date(completion.auto_accept_at);
    const now = new Date();
    const timeRemaining = autoAcceptDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));

    if (daysRemaining <= 0) {
      return { message: 'Auto-accept has occurred', urgent: true };
    } else if (daysRemaining === 1) {
      return { message: 'Auto-accepts tomorrow', urgent: true };
    } else {
      return { message: `Auto-accepts in ${daysRemaining} days`, urgent: daysRemaining <= 2 };
    }
  };

  const countdown = getAutoAcceptCountdown();

  const hasRequiredEvidence = () => {
    return documents.some(doc => 
      doc.content_type.includes('shipment') || doc.content_type.includes('delivery')
    );
  };

  const handleSubmitReview = async () => {
    if (!reviewResult) {
      toast({
        title: "Review required",
        description: "Please select to accept or reject the completion",
        variant: "destructive",
      });
      return;
    }

    if (reviewResult === 'rejected' && !reasonCode) {
      toast({
        title: "Reason required",
        description: "Please select a reason for rejection",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);

      await reviewCompletion(
        reviewResult,
        reasonCode || undefined,
        reviewNotes || undefined
      );

      // Track job completion when accepted
      if (reviewResult === 'accepted') {
        analytics.track({
          name: ANALYTICS_EVENTS.JOB_COMPLETED,
          category: 'transaction',
          properties: {
            job_id: job.id,
            total_value_cents: job.budget_cents,
          },
        });
      }

      toast({
        title: `Completion ${reviewResult}`,
        description: reviewResult === 'accepted' 
          ? "The job has been marked as completed successfully"
          : "The vendor can now resubmit their work",
      });
    } catch (error) {
      toast({
        title: "Review failed",
        description: error instanceof Error ? error.message : 'Failed to submit review',
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Review Completion: {job.title}</h1>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>Job ID: {job.public_id}</span>
          <span>Budget: {formatCurrency(job.budget_cents)}</span>
          <Badge variant="secondary">{job.status.replace('_', ' ')}</Badge>
        </div>
      </div>

      {/* Auto-Accept Warning */}
      {completion.review_result === null && (
        <Card className={`mb-6 ${countdown.urgent ? 'border-destructive' : 'border-warning'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${countdown.urgent ? 'text-destructive' : 'text-warning'}`} />
              Review Deadline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              <strong>{countdown.message}</strong> - If you don't review by then, 
              the completion will be automatically accepted.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Auto-accept date: {new Date(completion.auto_accept_at).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Completion Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Completion Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Submitted Date</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(completion.submitted_at).toLocaleString()}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Vendor Completion Date</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {completion.delivery_date 
                  ? new Date(completion.delivery_date).toLocaleDateString()
                  : 'Not specified'}
              </p>
            </div>
          </div>

          {completion.completion_notes && (
            <>
              <Separator />
              <div>
                <Label className="text-sm font-medium">Vendor Notes</Label>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                  {completion.completion_notes}
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Evidence Gallery */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Evidence Documentation
            <Badge variant={hasRequiredEvidence() ? "default" : "destructive"}>
              {documents.length} documents
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <DocumentViewer
              documents={documents}
              entityType="completion"
              entityId={completion.id}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <XCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No documentation provided</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Form */}
      {completion.review_result === null && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Your Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="text-base font-medium mb-4 block">Review Decision</Label>
              <RadioGroup
                value={reviewResult}
                onValueChange={(value) => setReviewResult(value as 'accepted' | 'rejected')}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="accepted" id="accepted" />
                  <Label htmlFor="accepted" className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Accept Completion
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="rejected" id="rejected" />
                  <Label htmlFor="rejected" className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    Reject Completion
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {reviewResult === 'rejected' && (
              <div>
                <Label htmlFor="reason">Reason for Rejection</Label>
                <Select
                  value={reasonCode}
                  onValueChange={(value) => setReasonCode(value as ReviewReasonCode)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEW_REASON_CODES.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="review-notes">
                Additional Notes {reviewResult === 'rejected' ? '(Required for rejection)' : '(Optional)'}
              </Label>
              <Textarea
                id="review-notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={
                  reviewResult === 'rejected'
                    ? "Please explain what needs to be corrected for resubmission..."
                    : "Any additional feedback or comments..."
                }
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                onClick={handleSubmitReview}
                disabled={!reviewResult || submitting}
                variant={reviewResult === 'accepted' ? 'default' : 'destructive'}
                size="lg"
              >
                {submitting ? 'Submitting...' : `${reviewResult === 'accepted' ? 'Accept' : 'Reject'} Completion`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review History */}
      {completion.review_result && (
        <Card>
          <CardHeader>
            <CardTitle>Review History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                {completion.review_result === 'accepted' ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={completion.review_result === 'accepted' ? 'default' : 'destructive'}>
                      {completion.review_result}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {completion.reviewed_at && new Date(completion.reviewed_at).toLocaleString()}
                    </span>
                  </div>
                  
                  {completion.review_reason_code && (
                    <p className="text-sm font-medium mb-1">
                      Reason: {REVIEW_REASON_CODES.find(r => r.value === completion.review_reason_code)?.label}
                    </p>
                  )}
                  
                  {completion.review_notes && (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {completion.review_notes}
                    </p>
                  )}
                </div>
              </div>

              {completion.review_result === 'rejected' && completion.resubmission_count < completion.max_resubmissions && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-sm font-medium">Resubmission Available</p>
                  <p className="text-sm text-muted-foreground">
                    The vendor can resubmit their work with corrections.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}